import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Tag } from 'antd';
import { AxiosError } from 'axios';
import { ColumnsType, SortOrder } from 'antd/es/table/interface';
import { IAppUser, IVoucher } from '../../../interfaces';

import { EmitError } from '../../../utils/EmitError';
import AppUsersService from '../../../services/http/services/AppUsersService';
import VouchersService from '../../../services/http/services/VouchersService';
import useAuth from '../../../hooks/useAuth';
import { formatDateStringToDateAndTime } from '../../../utils/Conversores';
import { useGetColumnSearchProps } from '../../../hooks/FilterTable/stringClearFiltros';
import { useGetColumnSearchPropsData } from '../../../hooks/FilterTable/date';
import TableActionButton from '../../../components/TableActionButton';
import { ThemeContext } from '../../../contexts/ThemeContext';

export interface ITableData extends IVoucher {
    key: number;
    reward_name: string;
}

export default function useViewAppUserVouchers() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const [appUser, setAppUser] = useState<IAppUser>();
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });
    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const { id } = useParams();
    const { activeCity } = useAuth();

    const navigate = useNavigate();

    // Call custom search hooks at top level
    const rewardSearchProps = useGetColumnSearchProps({ dataIndex: 'reward_name', ref: refFuncaoLimparFiltrosString });
    const codeSearchProps = useGetColumnSearchProps({ dataIndex: 'redemption_code', ref: refFuncaoLimparFiltrosString });
    const createdDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'created_at' });
    const validityDateSearchProps = useGetColumnSearchPropsData({ dataIndex: 'validity' });

    const columns: ColumnsType<ITableData> = useMemo(() => [
        {
            title: 'Recompensa',
            dataIndex: 'reward_name',
            key: 'reward_name',
            sorter: (a: ITableData, b: ITableData) => a?.reward?.name.localeCompare(b?.reward?.name || '') || 0,
            showSorterTooltip: false,
            ...rewardSearchProps,
            width: '28%',
            render: (name: string) => (
                <span style={{ fontWeight: 600, color: isDark ? '#f1f5f9' : '#1e293b' }}>
                    {name || '--'}
                </span>
            ),
        },
        {
            title: 'Código',
            dataIndex: 'redemption_code',
            key: 'redemption_code',
            ...codeSearchProps,
            render: (code: string) => (
                <span
                    style={{
                        fontFamily: 'monospace',
                        fontWeight: 700,
                        color: '#1677ff',
                        background: isDark ? 'rgba(22, 119, 255, 0.12)' : '#e6f4ff',
                        padding: '3px 8px',
                        borderRadius: 6,
                        letterSpacing: 1,
                    }}
                >
                    {code}
                </span>
            ),
        },
        {
            title: 'Pontos',
            dataIndex: 'points_used',
            key: 'points_used',
            align: 'right' as const,
            render: (points: number) => (
                <Tag color="blue" style={{ margin: 0, borderRadius: 12, fontWeight: 700 }}>
                    {(points || 0).toLocaleString('pt-BR')} P
                </Tag>
            ),
            sorter: (a: ITableData, b: ITableData) => (a?.points_used || 0) - (b?.points_used || 0),
            showSorterTooltip: false,
        },
        {
            title: 'Data de Resgate',
            dataIndex: 'created_at',
            key: 'created_at',
            sorter: (a: ITableData, b: ITableData) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
            showSorterTooltip: false,
            ...createdDateSearchProps,
            render: (data: any) => (
                <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: isDark ? '#cbd5e1' : '#475569' }}>
                    {data ? formatDateStringToDateAndTime(data) : '--'}
                </span>
            ),
            defaultSortOrder: 'descend' as keyof SortOrder,
        },
        {
            title: 'Expira',
            dataIndex: 'validity',
            key: 'validity',
            sorter: (a: ITableData, b: ITableData) => new Date(a.validity).getTime() - new Date(b.validity).getTime(),
            showSorterTooltip: false,
            ...validityDateSearchProps,
            render: (data: any) => (
                <span style={{ fontSize: 12.5, whiteSpace: 'nowrap', color: isDark ? '#cbd5e1' : '#475569' }}>
                    {data ? formatDateStringToDateAndTime(data) : '--'}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a: ITableData, b: ITableData) => a.status.localeCompare(b.status),
            filters: [
                { text: 'Emitido', value: 'Emitido' },
                { text: 'Resgatado', value: 'Resgatado' },
                { text: 'Expirado', value: 'Expirado' },
            ],
            onFilter: (value: any, record: IVoucher) => record.status === value,
            render: (status: string) => {
                const color = status === 'Resgatado' ? 'success' : status === 'Expirado' ? 'error' : 'processing';
                return (
                    <Tag
                        color={color}
                        style={{
                            borderRadius: 6,
                            fontWeight: 600,
                            padding: '2px 8px',
                        }}
                    >
                        {status}
                    </Tag>
                );
            },
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 70,
            render: (_: any, record: ITableData) => (
                <TableActionButton
                    action="view"
                    tooltip="Visualizar detalhes do voucher"
                    onClick={() => navigate(`/vouchers/${record.redemption_code}`)}
                />
            ),
        },
    ], [
        isDark,
        navigate,
        rewardSearchProps,
        codeSearchProps,
        createdDateSearchProps,
        validityDateSearchProps,
    ]);

    const setTableDataFromRequest = useCallback((data: Array<IVoucher>) => {
        if (!Array.isArray(data)) return;
        const newData: ITableData[] = data.map((voucher) => ({
            ...voucher,
            key: voucher.id,
            reward_name: voucher?.reward?.name || '',
        }));
        setTableData(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadData() {
            try {
                setIsLoading(true);

                if (!id || !activeCity) return;

                const cityId = activeCity?.id!;
                const appUserId = Number(id);
                const signal = controller.signal;

                const [vouchersList, appUserData] = await Promise.all([
                    VouchersService.listVouchersByAppUserAndCity({ appUserId, cityId, signal }),
                    AppUsersService.getAppUserById({ appUserId, signal }),
                ]);

                setTableDataFromRequest(vouchersList);
                setAppUser(appUserData);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }
                EmitError(err, true, 'viewAppUserVouchers_error');
                navigate('/usuarios/aplicativo');
            } finally {
                setIsLoading(false);
            }
        }

        loadData();

        return () => {
            controller.abort();
        };
    }, [id, activeCity, navigate, setTableDataFromRequest]);

    const handleClickGoBack = useCallback(() => {
        navigate(id ? `/usuarios/aplicativo/visualizar/${id}` : '/usuarios/aplicativo');
    }, [id, navigate]);

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    return {
        appUser,
        tableData,
        isLoading,
        pagination,
        columns,
        handleClickGoBack,
        handlePaginationChange,
        navigate,
    };
}
